/**
 * atomNaming - Shared atom naming utilities for accessibility
 *
 * Provides helpers to generate human-readable atom names with feature context
 * for use in accessibility descriptions (stereo spatial, curved arrows, etc.).
 *
 * Resolution order:
 *   1. If `featureContextId` is supplied AND the atom is in that feature →
 *      that feature's per-feature label (`feature.atomLabels[i]`).
 *   2. Otherwise → the default-feature label from
 *      `@alchemiesolns/feature-extractor` (highest type priority).
 *
 * The simple-molecule lowercase override (atom-type features with ≤4 atoms,
 * e.g. "oxygen of H₂O") is preserved here since it is display policy specific
 * to spoken/aria phrasing.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-90, PIPH-97
 */

import {
  defaultFeatureLabel,
  featureLabel,
  getElementName,
  type SerializationResult,
} from '@alchemiesolns/feature-extractor';

/**
 * Element symbol to full name mapping for the simple-molecule fallback.
 */
const elementNames: Record<string, string> = {
  C: 'Carbon',
  H: 'Hydrogen',
  O: 'Oxygen',
  N: 'Nitrogen',
  S: 'Sulfur',
  P: 'Phosphorus',
  F: 'Fluorine',
  Cl: 'Chlorine',
  Br: 'Bromine',
  I: 'Iodine',
};

/**
 * Find which feature contains a given atom index and return the atom's
 * name with feature context (e.g., "Carbon-2 of 4-carbon chain").
 *
 * When `featureContextId` is provided and the atom is in that feature, that
 * feature's per-feature label is used. Otherwise the atom's default feature
 * (highest priority by type: ring > bridged_bicyclic > chain > atom_cluster
 * > functional_group > atom) provides the label.
 *
 * @example
 * getAtomNameWithContext(2, serialized)
 * // Returns: "Carbon-2 of 4-carbon chain"  (default feature)
 *
 * @example
 * getAtomNameWithContext(2, serialized, ketoneFeatureId)
 * // Returns: "Carbon of ketone"  (single C in the ketone scope)
 */
export function getAtomNameWithContext(
  atomIndex: number,
  serialized: SerializationResult,
  featureContextId?: string,
): string {
  const atom = serialized.atoms.find(a => a.index === atomIndex);
  if (!atom) {
    return `Atom-${atomIndex}`;
  }

  // PIPH-122: For simple molecules (atom-type features with ≤4 atoms),
  // use simplified lowercase element names without indices.
  // E.g., "oxygen of H₂O" instead of "Oxygen-1 of H₂O".
  // This applies regardless of whether a featureContextId is supplied —
  // when one is supplied we honor that feature; otherwise fall back to the
  // first atom-type feature the atom is in.
  const simpleFeature = serialized.features.find(
    f =>
      atom.featureIds.includes(f.id) &&
      f.type === 'atom' &&
      f.atomIndices.length <= 4 &&
      (!featureContextId || f.id === featureContextId),
  );
  if (simpleFeature) {
    const simpleName = elementNames[atom.symbol]?.toLowerCase() ?? atom.symbol.toLowerCase();
    return `${simpleName} of ${simpleFeature.name}`;
  }

  // Resolve the per-feature label. If the caller supplied a context and the
  // atom is in it, use that scope. Otherwise fall back to the default feature.
  if (featureContextId) {
    const scoped = featureLabel(serialized, atomIndex, featureContextId);
    const feature = serialized.features.find(f => f.id === featureContextId);
    if (scoped && feature) {
      return `${scoped} of ${feature.name}`;
    }
  }

  // Default-feature path: atom's highest-priority feature determines the
  // label and the "of {feature}" suffix.
  const label = defaultFeatureLabel(serialized, atomIndex);
  const defaultFeature = pickDefaultFeatureForDisplay(atomIndex, serialized);
  if (defaultFeature) {
    return `${label} of ${defaultFeature.name}`;
  }

  // Defensive fallback: atom outside any feature (should not occur for
  // serialized atoms; the serializer only emits atoms with feature membership).
  return elementNames[atom.symbol] ?? atom.symbol;
}

/**
 * Format an atom name with charge context when the atom has a non-zero charge.
 *
 * @param baseName - The base atom name (e.g., "Carbon-2 of 3-carbon chain")
 * @param charge - The formal charge on the atom
 * @returns Name with charge prefix if charged, otherwise unchanged
 *
 * @example
 * formatAtomNameWithCharge("Oxygen of alcohol", -1)
 * // Returns: "negatively charged Oxygen of alcohol"
 */
export function formatAtomNameWithCharge(baseName: string, charge: number): string {
  if (!charge) return baseName;
  const chargePrefix = charge > 0 ? 'positively charged' : 'negatively charged';
  return `${chargePrefix} ${baseName}`;
}

/**
 * Get atom name with both feature context and charge context.
 *
 * @example
 * getAtomNameWithChargeContext(2, serialized)
 * // For a charged oxygen: "negatively charged Oxygen of alcohol"
 */
export function getAtomNameWithChargeContext(
  atomIndex: number,
  serialized: SerializationResult,
  featureContextId?: string,
): string {
  const atom = serialized.atoms.find(a => a.index === atomIndex);
  const baseName = getAtomNameWithContext(atomIndex, serialized, featureContextId);

  if (!atom) return baseName;

  return formatAtomNameWithCharge(baseName, atom.charge || 0);
}

/**
 * PIPH-310: Format an element name for mid-sentence prose with charge +
 * radical adjectivization.
 *
 * Rules:
 *   charge > 0   → "positively charged {name}"
 *   charge < 0   → "negatively charged {name}"
 *   radicals > 0 → append " radical"
 *   no charge & no radical → "{name}"
 *
 * Both adjectives may apply: charge=+1 with radicals=1 →
 * "positively charged oxygen radical". Charge magnitude does not change the
 * adjective; radical count does not pluralize the suffix.
 *
 * Element name resolves via `getElementName` (lowercased); unknown symbols
 * fall back to the lowercased symbol.
 *
 * Distinct from:
 *   - `formatAtomNameWithCharge`: operates on already-resolved "X of feature"
 *     strings (no radical handling, no element-name lookup).
 *   - `describeAtom` (atomDescription.ts §6.2): per-atom full description.
 *
 * This utility is for templates that name an atom by element in prose:
 * §4.1 base sentence heteroatom append, §4.3 connection bridge atom,
 * feature-block singleton/cluster lines.
 */
export function elementDisplayName(
  symbol: string,
  opts?: { charge?: number; radicals?: number },
): string {
  const fullName = getElementName(symbol);
  const baseName = (fullName ?? symbol).toLowerCase();
  const charge = opts?.charge ?? 0;
  const radicals = opts?.radicals ?? 0;

  let result: string;
  if (charge > 0) {
    result = `positively charged ${baseName}`;
  } else if (charge < 0) {
    result = `negatively charged ${baseName}`;
  } else {
    result = baseName;
  }

  if (radicals > 0) {
    result += ' radical';
  }

  return result;
}

const FEATURE_TYPE_PRIORITY: Record<string, number> = {
  ring: 6,
  bridged_bicyclic: 5,
  chain: 4,
  atom_cluster: 3,
  molecule: 2,
  functional_group: 1,
  atom: 0,
};

function pickDefaultFeatureForDisplay(
  atomIndex: number,
  serialized: SerializationResult,
) {
  const atom = serialized.atoms.find(a => a.index === atomIndex);
  if (!atom) return undefined;
  let best: typeof serialized.features[number] | undefined;
  let bestPriority = -1;
  for (const fid of atom.featureIds) {
    const f = serialized.features.find(x => x.id === fid);
    if (!f) continue;
    const priority = FEATURE_TYPE_PRIORITY[f.type] ?? -1;
    if (priority > bestPriority) {
      best = f;
      bestPriority = priority;
    }
  }
  return best;
}
